import { useEffect, useMemo, useState } from "react";
import {
  useGetOrganizationsQuery,
  useGetUsersQuery,
  useOrganizationUsersQuery,
  useLazyFindUserByEmailQuery,
  useCreateUserMutation,
  useAddUserToOrganizationMutation,
  useUpdateUserOrganizationMutation,
  useRemoveUserFromOrganizationMutation,
} from "../../redux/api";
import type { UserOrganization, UserLookup } from "../../redux/api";
import { Modal } from "../../components/Modal";
import { Field } from "../../components/Field";
import { IconPlus, IconSearch, IconEdit, IconTrash, IconEye, IconEyeOff } from "../../components/icons";
import { validateEmail, validatePhone } from "../../utils/validation";
import { PhoneInput } from "../../components/PhoneInput";
import { PLATFORM } from "../../config/platform";
import { RowMenu } from "../../components/RowMenu";

const COLUMN_COUNT = 6;

// An organization has exactly one owner (set when the org is created), so the
// owner role is not assignable — or editable — here.
const ROLE_OPTIONS = ["admin", "manager", "employee"];
const STATUS_OPTIONS = ["active", "suspended"];

const isOwnerMembership = (m: UserOrganization) =>
  (m.roles ?? []).some((r) => r.toLowerCase() === "owner");

const statusTone = (status?: string | null): string => {
  switch ((status ?? "").toLowerCase()) {
    case "active":
      return "badge--success";
    case "invited":
    case "pending":
      return "badge--info";
    case "suspended":
      return "badge--warning";
    case "deleted":
      return "badge--danger";
    default:
      return "badge--info";
  }
};

const formatDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString() : "—";

// Generates a readable-but-strong random password (letters, digits, symbol).
const generatePassword = (): string => {
  const chars =
    "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%";
  let out = "";
  for (let i = 0; i < 12; i++) {
    out += chars[Math.floor(Math.random() * chars.length)];
  }
  return out;
};

type NewUser = {
  prefix: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
};

const emptyNewUser: NewUser = {
  prefix: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  password: "",
};

type EditForm = {
  status: string;
  roles: string[];
};

export interface OrganizationUsersScreenProps {
  /** Which role this filtered view shows — "Managers" or "Employees". */
  roleFilter: "manager" | "employee";
}

// Organization → Managers / Employees (shared engine, filtered by role).
// Owners/admins view members of an organization matching `roleFilter`, add
// users (existing or newly created), and edit a member's status/roles or
// remove them — except the org's owner membership, which stays protected.
export const OrganizationUsersScreen = ({ roleFilter }: OrganizationUsersScreenProps) => {
  const roleLabel = roleFilter === "manager" ? "Managers" : "Employees";
  const { data: organizations = [] } = useGetOrganizationsQuery();
  const { data: allUsers = [] } = useGetUsersQuery();

  const [orgId, setOrgId] = useState<string>("");

  useEffect(() => {
    if (!orgId && organizations.length) setOrgId(organizations[0].id);
  }, [organizations, orgId]);

  const {
    data: members = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useOrganizationUsersQuery(orgId, { skip: !orgId });

  const [findUserByEmail, { isFetching: lookingUp }] =
    useLazyFindUserByEmailQuery();
  const [createUser, { isLoading: creatingUser }] = useCreateUserMutation();
  const [addUserToOrganization, { isLoading: adding }] =
    useAddUserToOrganizationMutation();
  const [updateUserOrganization, { isLoading: updatingMember }] =
    useUpdateUserOrganizationMutation();
  const [removeUserFromOrganization, { isLoading: removing }] =
    useRemoveUserFromOrganizationMutation();

  const [open, setOpen] = useState(false);
  // Email-first flow: look up the email first, then either invite the
  // existing account (no password touched) or create a brand-new one.
  const [step, setStep] = useState<"email" | "existing" | "new">("email");
  const [lookupEmail, setLookupEmail] = useState("");
  const [lookupResult, setLookupResult] = useState<UserLookup | null>(null);
  const [roles, setRoles] = useState<string[]>([roleFilter]);
  const [newUser, setNewUser] = useState<NewUser>(emptyNewUser);
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  // Per-field validation messages — shown directly under the offending input
  // (with a red border) instead of the single top-of-form banner.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});

  const [editing, setEditing] = useState<UserOrganization | null>(null);
  const [editForm, setEditForm] = useState<EditForm>({ status: "active", roles: [] });
  const [editError, setEditError] = useState<string | null>(null);


  const saving = creatingUser || adding;

  const [search, setSearch] = useState("");

  const userById = (id: string) => allUsers.find((u) => u.id === id);

  // Only members who actually hold the role this screen is scoped to.
  const roleMembers = useMemo(
    () => members.filter((m) => (m.roles ?? []).includes(roleFilter)),
    [members, roleFilter],
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return roleMembers;
    return roleMembers.filter((m: UserOrganization) => {
      const u = userById(m.userId);
      const name = u ? `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() : "";
      return [name, u?.email, m.status, ...(m.roles ?? [])].some((field) =>
        (field ?? "").toLowerCase().includes(q),
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleMembers, search, allUsers]);

  const openAdd = () => {
    setStep("email");
    setLookupEmail("");
    setLookupResult(null);
    setRoles([roleFilter]);
    setNewUser(emptyNewUser);
    setShowPassword(false);
    setFormError(null);
    setFieldErrors({});
    setOpen(true);
  };

  const toggleRole = (role: string) => {
    setRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );
    setFieldErrors((prev) => (prev.role ? { ...prev, role: undefined } : prev));
  };

  const setNewUserField = (key: keyof NewUser, value: string) => {
    setNewUser((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  // Step 1 → 2: resolve whether this email is an existing account or new.
  const handleLookup = async () => {
    setFormError(null);

    const emailError = validateEmail(lookupEmail);
    if (emailError) {
      setFormError(emailError);
      return;
    }

    try {
      const result = await findUserByEmail(lookupEmail.trim()).unwrap();

      if (result.exists && result.alreadyInOrg) {
        setFormError("This person is already a member of your organization.");
        return;
      }

      setLookupResult(result);
      if (result.exists) {
        setStep("existing");
      } else {
        setNewUser({ ...emptyNewUser, email: lookupEmail.trim() });
        setStep("new");
      }
    } catch (err) {
      setFormError(
        (err as { message?: string })?.message ?? "Failed to look up email.",
      );
    }
  };

  const handleChangeEmail = () => {
    setStep("email");
    setLookupResult(null);
    setFormError(null);
    setFieldErrors({});
  };

  // Step 2: invite the existing account (no password involved) or create +
  // invite a brand-new one.
  const handleAdd = async () => {
    setFormError(null);

    if (!orgId) return;

    const errors: Record<string, string> = {};
    if (roles.length === 0) {
      errors.role = "Select at least one role.";
    }

    if (step === "existing") {
      if (!lookupResult?.id) {
        errors.user = "Select a user to add.";
      }
    } else {
      if (!newUser.firstName.trim()) errors.firstName = "First name is required.";
      if (!newUser.lastName.trim()) errors.lastName = "Last name is required.";

      const phoneError = validatePhone(newUser.phone);
      if (phoneError) errors.phone = phoneError;

      if (!newUser.password.trim() || newUser.password.trim().length < 8) {
        errors.password = "Password must be at least 8 characters.";
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    try {
      let userId: string;

      if (step === "existing") {
        userId = lookupResult!.id!;
      } else {
        const created = await createUser({
          prefix: newUser.prefix.trim() || undefined,
          firstName: newUser.firstName.trim(),
          lastName: newUser.lastName.trim(),
          email: newUser.email.trim(),
          phone: newUser.phone.trim() || undefined,
          password: newUser.password.trim(),
        }).unwrap();

        userId = created.id;
      }

      await addUserToOrganization({
        userId,
        organizationId: orgId,
        roles,
      }).unwrap();

      setOpen(false);
    } catch (err) {
      setFormError(
        (err as { message?: string })?.message ?? "Failed to add user.",
      );
    }
  };

  const openEdit = (member: UserOrganization) => {
    setEditing(member);
    setEditForm({ status: member.status ?? "active", roles: member.roles ?? [] });
    setEditError(null);
  };

  const toggleEditRole = (role: string) =>
    setEditForm((prev) => ({
      ...prev,
      roles: prev.roles.includes(role)
        ? prev.roles.filter((r) => r !== role)
        : [...prev.roles, role],
    }));

  const handleSaveEdit = async () => {
    if (!editing) return;
    setEditError(null);

    if (editForm.roles.length === 0) {
      setEditError("Select at least one role.");
      return;
    }

    try {
      await updateUserOrganization({
        id: editing.id,
        input: { status: editForm.status, roles: editForm.roles },
      }).unwrap();
      setEditing(null);
    } catch (err) {
      setEditError(
        (err as { message?: string })?.message ?? "Failed to update member.",
      );
    }
  };

  const handleRemove = async (member: UserOrganization) => {
    const u = userById(member.userId);
    const name = u ? `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() : member.userId;
    if (!window.confirm(`Remove "${name || member.userId}" from this organization?`)) {
      return;
    }
    try {
      await removeUserFromOrganization(member.id).unwrap();
    } catch (err) {
      window.alert(
        (err as { message?: string })?.message ?? "Failed to remove member.",
      );
    }
  };

  const errorMessage = error
    ? (error as { message?: string }).message ?? `Failed to load ${roleLabel.toLowerCase()}`
    : null;

  return (
    <div className="dashboard">
      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>{roleLabel}</h2>
            <p className="panel__sub">
              Showing {visible.length} of {roleMembers.length}
            </p>
          </div>
          <div className="toolbar">
            <div className="search">
              <span className="search__icon">
                <IconSearch />
              </span>
              <input
                type="search"
                placeholder={`Search ${roleLabel.toLowerCase()}…`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <select
              className="select"
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
            >
              {organizations.length === 0 ? (
                <option value="">No organizations</option>
              ) : (
                organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))
              )}
            </select>
            <button
              type="button"
              className="btn"
              onClick={() => refetch()}
              disabled={isFetching || !orgId}
            >
              {isFetching ? "Refreshing…" : "Refresh"}
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={openAdd}
              disabled={!orgId}
            >
              <IconPlus /> Add user
            </button>
          </div>
        </div>

        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Roles</th>
                <th>Status</th>
                <th>Employee type</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {!orgId ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    Select an organization to view its {roleLabel.toLowerCase()}.
                  </td>
                </tr>
              ) : isLoading ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    Loading {roleLabel.toLowerCase()}…
                  </td>
                </tr>
              ) : errorMessage ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    <div className="table-error">
                      <span>{errorMessage}</span>
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => refetch()}
                      >
                        Try again
                      </button>
                    </div>
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    {roleMembers.length === 0
                      ? `No ${roleLabel.toLowerCase()} yet. Add the first user.`
                      : `No ${roleLabel.toLowerCase()} match your search.`}
                  </td>
                </tr>
              ) : (
                visible.map((m: UserOrganization) => {
                  const u = userById(m.userId);
                  const name = u
                    ? `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim()
                    : m.userId;
                  const isOwner = isOwnerMembership(m);
                  return (
                    <tr key={m.id}>
                      <td>
                        <div className="cell-title">{name || m.userId}</div>
                        {u ? (
                          <div className="cell-date">{u.email}</div>
                        ) : null}
                      </td>
                      <td>
                        {(m.roles ?? []).map((role) => (
                          <span key={role} className="pill pill--neutral">
                            {role}
                          </span>
                        ))}
                      </td>
                      <td>
                        <span className={`badge ${statusTone(m.status)}`}>
                          <span className="badge__dot" />
                          {m.status ?? "—"}
                        </span>
                      </td>
                      <td>{m.employeeType ?? "—"}</td>
                      <td className="cell-date">{formatDate(m.joinedAt)}</td>
                      <td>
                        {isOwner ? (
                          <span className="field__readonly">Owner</span>
                        ) : (
                          <RowMenu
                            items={[
                              {
                                label: "Edit",
                                icon: <IconEdit size={16} />,
                                onSelect: () => openEdit(m),
                              },
                              {
                                label: "Remove",
                                icon: <IconTrash size={16} />,
                                onSelect: () => handleRemove(m),
                                danger: true,
                                disabled: removing,
                              },
                            ]}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>


      <Modal
        open={open}
        title="Add user to organization"
        onClose={() => setOpen(false)}
        footer={
          step === "email" ? (
            <>
              <button
                type="button"
                className="btn"
                onClick={() => setOpen(false)}
                disabled={lookingUp}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--primary"
                onClick={handleLookup}
                disabled={lookingUp || !lookupEmail.trim()}
              >
                {lookingUp ? "Checking…" : "Continue"}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="btn"
                onClick={() => setOpen(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--primary"
                onClick={handleAdd}
                disabled={saving}
              >
                {saving
                  ? "Adding…"
                  : step === "existing"
                    ? "Add to organization"
                    : "Create & add"}
              </button>
            </>
          )
        }
      >
        {formError ? <div className="form-error">{formError}</div> : null}

        {step === "email" ? (
          <div className="form-grid">
            <label className="field field--full">
              Email *
              <input
                type="email"
                value={lookupEmail}
                onChange={(e) => setLookupEmail(e.target.value)}
                placeholder="person@example.com"
                autoFocus
              />
            </label>
            <p className="panel__sub field--full" style={{ margin: 0 }}>
              If this email already has an {PLATFORM.platform_name} account, you'll invite
              it as-is — their existing password stays theirs. Otherwise
              you'll create a new account for them.
            </p>
          </div>
        ) : (
          <>
            <div className="toolbar" style={{ marginBottom: "1rem" }}>
              <span className="pill pill--neutral">{lookupEmail}</span>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={handleChangeEmail}
              >
                ← Change email
              </button>
            </div>

            {step === "existing" ? (
              <div className="form-grid">
                <Field label="Name" error={fieldErrors.user} full>
                  <input
                    value={
                      `${lookupResult?.firstName ?? ""} ${lookupResult?.lastName ?? ""}`.trim() ||
                      "—"
                    }
                    disabled
                  />
                  <span className="field__readonly">
                    Existing account — no password is created or changed
                  </span>
                </Field>
              </div>
            ) : (
              <div className="form-grid">
                <label className="field">
                  Prefix
                  <input
                    value={newUser.prefix}
                    onChange={(e) => setNewUserField("prefix", e.target.value)}
                    placeholder="Mr / Ms"
                  />
                </label>
                <Field label="Phone" error={fieldErrors.phone}>
                  <PhoneInput
                    value={newUser.phone}
                    onChange={(v) => setNewUserField("phone", v)}
                  />
                </Field>
                <Field label="First name *" error={fieldErrors.firstName}>
                  <input
                    value={newUser.firstName}
                    onChange={(e) => setNewUserField("firstName", e.target.value)}
                  />
                </Field>
                <Field label="Last name *" error={fieldErrors.lastName}>
                  <input
                    value={newUser.lastName}
                    onChange={(e) => setNewUserField("lastName", e.target.value)}
                  />
                </Field>
                <Field label="Password *" error={fieldErrors.password} full>
                  <div className="phone-input">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={newUser.password}
                      onChange={(e) => setNewUserField("password", e.target.value)}
                      placeholder="At least 8 characters"
                    />
                  </div>
                  <div className="toolbar" style={{ marginTop: "0.4rem" }}>
                    <button
                      type="button"
                      className="btn btn--ghost"
                      onClick={() => setNewUserField("password", generatePassword())}
                    >
                      Generate
                    </button>
                    <button
                      type="button"
                      className="icon-btn"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      onClick={() => setShowPassword((v) => !v)}
                    >
                      {showPassword ? <IconEyeOff size={16} /> : <IconEye size={16} />}
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost"
                      onClick={() => {
                        if (newUser.password) {
                          navigator.clipboard?.writeText(newUser.password);
                        }
                      }}
                      disabled={!newUser.password}
                    >
                      Copy
                    </button>
                  </div>
                </Field>
              </div>
            )}

            <div style={{ marginTop: "1rem" }}>
              <Field label="Roles" error={fieldErrors.role} full>
                <div className="toolbar" style={{ flexWrap: "wrap" }}>
                  {ROLE_OPTIONS.map((role) => (
                    <label
                      key={role}
                      className="pill pill--neutral"
                      style={{ cursor: "pointer", textTransform: "capitalize" }}
                    >
                      <input
                        type="checkbox"
                        checked={roles.includes(role)}
                        onChange={() => toggleRole(role)}
                        style={{ marginRight: "0.4rem" }}
                      />
                      {role}
                    </label>
                  ))}
                </div>
              </Field>
            </div>
          </>
        )}
      </Modal>

      <Modal
        open={!!editing}
        title="Edit member"
        onClose={() => setEditing(null)}
        footer={
          <>
            <button
              type="button"
              className="btn"
              onClick={() => setEditing(null)}
              disabled={updatingMember}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={handleSaveEdit}
              disabled={updatingMember}
            >
              {updatingMember ? "Saving…" : "Save changes"}
            </button>
          </>
        }
      >
        {editError ? <div className="form-error">{editError}</div> : null}

        <div className="form-grid">
          <label className="field">
            Status
            <select
              value={editForm.status}
              onChange={(e) =>
                setEditForm((prev) => ({ ...prev, status: e.target.value }))
              }
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="field field--full" style={{ marginTop: "1rem" }}>
          <span>Roles</span>
          <div className="toolbar" style={{ flexWrap: "wrap" }}>
            {ROLE_OPTIONS.map((role) => (
              <label
                key={role}
                className="pill pill--neutral"
                style={{ cursor: "pointer", textTransform: "capitalize" }}
              >
                <input
                  type="checkbox"
                  checked={editForm.roles.includes(role)}
                  onChange={() => toggleEditRole(role)}
                  style={{ marginRight: "0.4rem" }}
                />
                {role}
              </label>
            ))}
          </div>
        </div>
      </Modal>
    </div>
  );
};
